import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  TextField,
  Radio,
  RadioGroup,
  Typography,
  Chip,
  IconButton,
  FormLabel,
} from "@mui/material";
import { Receipt, MonitorWeight, Close } from "@mui/icons-material";
import { LoadingButton } from "../../../components/shared/LoadingButton";
import PalletsIcon from "../../../components/shared/icons/PalletsIcon";
import BoxesIcon from "../../../components/shared/icons/BoxesIcon";

const radioButtonLikeSx = {
  flex: 1,
  minWidth: 0,
  margin: 0,
  justifyContent: "flex-start",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  px: 1,
  py: 0.5,
  transition: "border-color 0.2s, background-color 0.2s, color 0.2s",
  "& .MuiFormControlLabel-label": {
    lineHeight: 1,
  },
  "& .MuiRadio-root": {
    p: 0.5,
    mr: 0.5,
  },
  "&:hover": {
    borderColor: "primary.main",
    backgroundColor: "action.hover",
  },
  "& .MuiRadio-root.Mui-checked + .MuiFormControlLabel-label": {
    borderColor: "primary.main",
    color: "primary.main",
    fontWeight: 600,
  },
  "&:has(.MuiRadio-root.Mui-checked)": {
    borderColor: "primary.main",
    backgroundColor: "action.selected",
  },
  "&:has(.MuiRadio-root.Mui-focusVisible)": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2,
  },
} as const;

type AssignToDialogV2Props = {
  open: boolean;
  onClose?: () => void;
};

const AssignToDialogV2 = ({ open, onClose }: AssignToDialogV2Props) => {
  const [selectedOption, setSelectedOption] = useState("delivery_route");

  const mockRoutes = ["Full 1", "Full 2"];
  const mockTraps = ["TRAP-39434", "OVERFLOW TRAILER"];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            height: "600px",
          },
        },
      }}
    >
      <DialogTitle>Assign 2 shipments</DialogTitle>
      <Box position="absolute" top={10} right={10}>
        <IconButton onClick={onClose} aria-label="close dialog">
          <Close />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          width: "550px",
        }}
      >
        <Box>
          <Typography variant="body1">
            Choose where to assign the selected shipments
          </Typography>
          <FormControl component="fieldset" sx={{ my: 2, width: "100%" }}>
            <RadioGroup
              row
              name="assignTo"
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              sx={{ width: "100%", gap: 1 }}
            >
              <FormControlLabel
                value="delivery_route"
                control={<Radio />}
                label="Route"
                sx={radioButtonLikeSx}
              />
              <FormControlLabel
                value="delivery_trap"
                control={<Radio />}
                label="Trap"
                sx={radioButtonLikeSx}
              />
              <FormControlLabel
                value="osad"
                control={<Radio />}
                label="OSAD"
                sx={radioButtonLikeSx}
              />
            </RadioGroup>
          </FormControl>
        </Box>
        {(selectedOption === "delivery_route" ||
          selectedOption === "delivery_trap") && (
          <>
            <TextField
              id="search-routes"
              label={
                selectedOption === "delivery_route"
                  ? "Search Routes"
                  : "Search Traps"
              }
              variant="outlined"
              sx={{ mb: 3, mt: 1 }}
            />
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderLeft: 0,
                borderRight: 0,
                p: 2,
              }}
              aria-label="Assigned Shipments List"
            >
              {(selectedOption === "delivery_route"
                ? mockRoutes
                : mockTraps
              ).map((element) => (
                <Box sx={{ mb: 2 }} key={element}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Typography fontSize={15} fontWeight={600}>
                      {element}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label="CPLN"
                        size="small"
                        sx={(theme) => ({
                          backgroundColor: theme.palette.success.main,
                          color: theme.palette.common.white,
                          fontWeight: "bold",
                          fontSize: 12,
                          height: 24,
                        })}
                      />
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Box display="flex" alignItems="center" gap={1.5} mt={0.5}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Receipt
                          sx={{ width: 16, height: 16, color: "gray" }}
                        />
                        <Typography fontSize={12}>3 Bills</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PalletsIcon
                          sx={{ width: 16, height: 16, color: "gray" }}
                        />
                        <Typography fontSize={12}>3 PLts</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <BoxesIcon width={16} height={16} fillColor="gray" />
                        <Typography fontSize={12}>2 Pcs</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <MonitorWeight
                          sx={{ width: 16, height: 16, color: "gray" }}
                        />
                        <Typography fontSize={12}>100 lbs</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
        {selectedOption === "osad" && (
          <Box sx={{ pt: 1 }}>
            <FormLabel id="osad-option-label">Select an option</FormLabel>
            <RadioGroup
              aria-labelledby="osad-option-label"
              defaultValue="all-short"
              sx={{ mt: 3, gap: 3, display: "flex", flexDirection: "column" }}
            >
              <FormControlLabel
                value="all-short"
                control={<Radio />}
                label={
                  <Box>
                    <Typography fontSize={14} fontWeight={600}>
                      All short
                    </Typography>
                    <Typography fontSize={14} color="text.secondary">
                      Marks as all short and places the shipment in OSAD for
                      visibility
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="other"
                control={<Radio />}
                label={
                  <Box>
                    <Typography fontSize={14} fontWeight={600}>
                      Other
                    </Typography>
                    <Typography fontSize={14} color="text.secondary">
                      Moves shipments to the OSAD section
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ margin: "0 20px 20px 20px" }}>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton loadingLabel="Assigning..." isLoading={false}>
          Assign
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AssignToDialogV2;
